/**
 * Ratios Tab Component
 * Displays financial ratios analysis across multiple years
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Calculator, HelpCircle } from 'lucide-react';
import { calculateRatios } from '../../utils/valuationCalculations';

const HelpTooltip = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle size={14} className="inline text-blue-600 cursor-help" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg bottom-full left-1/2 -translate-x-1/4 mb-1 pointer-events-none">
      {text}
    </div>
  </div>
);

HelpTooltip.propTypes = {
  text: PropTypes.string.isRequired
};

const RatiosTab = ({ years, financialData }) => {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Calculator className="text-blue-600" size={24} />
        Financial Ratios Analysis
      </h3>

      <div>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left px-3 py-2 border font-semibold">Ratio</th>
              {years.map(year => (
                <th key={year} className="px-3 py-2 border text-center font-semibold">{year}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Profitability Ratios Section */}
            <tr className="bg-blue-100">
              <td colSpan={years.length + 1} className="px-3 py-1.5 border font-bold text-blue-900">
                💰 PROFITABILITY RATIOS
              </td>
            </tr>
            <tr className="bg-white">
              <td className="px-3 py-2 border font-medium">
                Gross Margin (%)
                <HelpTooltip text="Gross Profit / Revenue. Shows percentage of revenue retained after COGS. Higher is better (>40% is excellent)." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.grossMargin > 40 ? 'text-green-600' : ratios.grossMargin > 20 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.grossMargin.toFixed(1)}%
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-3 py-2 border font-medium">
                EBITDA Margin (%)
                <HelpTooltip text="EBITDA / Revenue. Shows operating profitability. >15% is good, >25% is excellent." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.ebitdaMargin > 25 ? 'text-green-600' : ratios.ebitdaMargin > 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.ebitdaMargin.toFixed(1)}%
                    </span>
                  </td>
                );
              })}
            </tr>

            {/* Liquidity Ratios Section */}
            <tr className="bg-cyan-100">
              <td colSpan={years.length + 1} className="px-3 py-1.5 border font-bold text-cyan-900">
                💧 LIQUIDITY RATIOS
              </td>
            </tr>
            <tr className="bg-white">
              <td className="px-3 py-2 border font-medium">
                Current Ratio
                <HelpTooltip text="Current Assets / Short-term Debt. Measures ability to pay short-term obligations. >1.5 is healthy, >2.0 is strong." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.currentRatio > 2 ? 'text-green-600' : ratios.currentRatio > 1 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.currentRatio.toFixed(2)}x
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-3 py-2 border font-medium">
                Cash Ratio
                <HelpTooltip text="Cash / Short-term Debt. Most conservative liquidity measure. >0.5 is good." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.cashRatio > 0.5 ? 'text-green-600' : ratios.cashRatio > 0.2 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.cashRatio.toFixed(2)}x
                    </span>
                  </td>
                );
              })}
            </tr>

            {/* Leverage Ratios Section */}
            <tr className="bg-purple-100">
              <td colSpan={years.length + 1} className="px-3 py-1.5 border font-bold text-purple-900">
                ⚖️ LEVERAGE RATIOS
              </td>
            </tr>
            <tr className="bg-white">
              <td className="px-3 py-2 border font-medium">
                Debt-to-Equity
                <HelpTooltip text="Total Liabilities / Equity. Shows financial leverage. <1.0 is conservative, <2.0 is moderate, >2.0 is aggressive." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.debtToEquity < 1 ? 'text-green-600' : ratios.debtToEquity < 2 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.debtToEquity.toFixed(2)}x
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-3 py-2 border font-medium">
                Debt-to-Assets (%)
                <HelpTooltip text="Total Liabilities / Total Assets. Shows percentage of assets financed by debt. <40% is low leverage, <60% is moderate, >60% is high leverage." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.debtToAssets < 40 ? 'text-green-600' : ratios.debtToAssets < 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.debtToAssets.toFixed(1)}%
                    </span>
                  </td>
                );
              })}
            </tr>

            {/* Efficiency Ratios Section */}
            <tr className="bg-green-100">
              <td colSpan={years.length + 1} className="px-3 py-1.5 border font-bold text-green-900">
                ⚡ EFFICIENCY RATIOS
              </td>
            </tr>
            <tr className="bg-white">
              <td className="px-3 py-2 border font-medium">
                Asset Turnover
                <HelpTooltip text="Revenue / Total Assets. Measures how efficiently assets generate revenue. >1.0 is good, >2.0 is excellent. Higher indicates better asset utilization." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.assetTurnover > 2 ? 'text-green-600' : ratios.assetTurnover > 1 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.assetTurnover.toFixed(2)}x
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-3 py-2 border font-medium">
                Return on Assets (ROA)
                <HelpTooltip text="Net Income / Total Assets. Measures how efficiently a company uses its assets to generate profit. >5% is good, >10% is excellent. Higher indicates better asset efficiency." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.returnOnAssets > 10 ? 'text-green-600' : ratios.returnOnAssets > 5 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.returnOnAssets.toFixed(1)}%
                    </span>
                  </td>
                );
              })}
            </tr>
            <tr className="bg-white">
              <td className="px-3 py-2 border font-medium">
                Return on Equity (ROE)
                <HelpTooltip text="Net Income / Equity. Measures return generated on shareholders' equity. >15% is good, >20% is excellent. Higher indicates better returns for equity investors." />
              </td>
              {years.map(year => {
                const ratios = calculateRatios(financialData, year);
                return (
                  <td key={year} className="px-3 py-2 border text-center">
                    <span className={`font-bold ${ratios.returnOnEquity > 20 ? 'text-green-600' : ratios.returnOnEquity > 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {ratios.returnOnEquity.toFixed(1)}%
                    </span>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

RatiosTab.propTypes = {
  years: PropTypes.arrayOf(PropTypes.number).isRequired,
  financialData: PropTypes.object.isRequired
};

export default RatiosTab;
